import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AbandonedScreen } from "./components/AbandonedScreen";
import { AnswerPeriodEndedScreen } from "./components/AnswerPeriodEndedScreen";
import { InvalidLinkScreen } from "./components/InvalidLinkScreen";
import { RespondentHome } from "./components/RespondentHome";
import { SubmittedScreen } from "./components/SubmittedScreen";
import { SurveyForm } from "./components/SurveyForm";
import { useRespondSession } from "./hooks/useRespondSession";

/**
 * RespondPage — 回答者画面のエントリポイント。
 *
 * URL: /r/:campaignId/:respondentId
 * `?t=<token>` はサーバ (backend/respondents.VerifyHandler) が検証しセッション Cookie を
 * 発行した上で本 URL へ 303 リダイレクトするため、本画面には token が渡ってこない。
 * 認証状態は useRespondSession が GET /api/respondent-session の結果で判定する。
 *
 * session.status==="completed" は「提出済みの回答者が再度リンクを踏んだ」ケース (ADR-0024)。
 * token は消費済みでも Cookie は発行されるため valid セッションとして到達するが、
 * 通常フロー (ホーム → SurveyForm) には入れず SubmittedScreen を出す。
 *
 * session.status==="answer_closed" は「回答期限 (EndDate) 超過後にマジックリンクを踏んだ」
 * ケース (ADR-0032)。verify handler は Cookie を発行せず `?reason=answer_closed` 付きで
 * redirect するため、useRespondSession が同 query を検知して答える。トークン自体は無効化
 * されていない (invalid とは別状態) ため、「再送すれば回答できる」invalid の案内文言とは
 * 区別し、再送しても回答できないことが伝わる文言にする。
 *
 * session.status==="abandoned" は「未受検 (abandoned) にされた respondent がリンクを踏んだ」
 * ケース (ADR-0054 Decision 3)。answer_closed と同じ「Cookie 未発行 + reason クエリ」の
 * 仕組みで表現され、AbandonedScreen (対象外になった旨の案内) に倒す。
 *
 * ホーム → アンケート本体 (SurveyForm) → Review → Thanks の 4 段階を、
 * RespondPage では「ホームか否か」のみ管理し、それ以外は SurveyForm 内で進行を持つ。
 */
export function RespondPage() {
  const { t } = useTranslation("respond");
  const { campaignId, respondentId, session } = useRespondSession();
  const [started, setStarted] = useState(false);

  if (session.status === "loading") {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10">
        <p className="text-sm text-muted-foreground">{t("session.loading")}</p>
      </main>
    );
  }

  if (session.status === "answer_closed") {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10">
        <AnswerPeriodEndedScreen />
      </main>
    );
  }

  if (session.status === "abandoned") {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10">
        <AbandonedScreen />
      </main>
    );
  }

  if (session.status === "invalid") {
    return (
      <main className="mx-auto max-w-4xl px-6 py-10">
        <InvalidLinkScreen campaignId={campaignId} respondentId={respondentId} />
      </main>
    );
  }

  const { fullName, companyName, status } = session.session;

  return (
    <main className="mx-auto max-w-4xl px-6 py-10">
      <header className="mb-10 border-b border-border pb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{companyName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("session.greeting", { fullName })}</p>
      </header>

      {status === "completed" ? (
        <SubmittedScreen />
      ) : started ? (
        <SurveyForm invitationId={respondentId} />
      ) : (
        <RespondentHome onStart={() => setStarted(true)} />
      )}
    </main>
  );
}
